import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import usePayrollEssentials from 'hooks/usePayrollEssentials'
import ViewDeductionsTab from './ViewDeductionsTab'
import ViewSalaryBreakdown from './ViewSalaryBreakdown'
import EditDeductionModal from '../EditDeductionModal'
import { ReactComponent as EditIcon } from "assets/images/icons/edit.svg"
import CustomDeductionModal from '../CustomDeductionModal'
import PaygroupsListTable from 'components/__new/grossPayroll/PaygroupsListTable'
import Maybe from 'components/__new/common/Maybe'
import Spinner from 'components/__new/common/Spinner'
import { assignPayStructureGroups, getPayStructureGroups, unassignPayStructureGroups, updatePayStructure } from 'store/entities/payroll/payrollSetup/action'
import { useLocation } from 'react-router-dom'

const ViewStructureDetails = () => {
    const location = useLocation()
    const {history, useSelector, dispatch, lettersRegex} = usePayrollEssentials();
    const breakdownSubmitRef = useRef();
    const deductionSubmitRef = useRef();

    const [isViewing, setIsViewing] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selected, setSelected] = useState([]);
    const [pensionShared, setPensionShared] = useState(true);

    const data = location?.state?.payload;
    const {customDeductions, selectedStructure, editPayStructureLoading: editLoading, structurePayGroupsLoading, structurePayGroupsData, unassignStructureGroupsLoading, assignStructureGroupsLoading} = useSelector(state => state.entities.payrollSetup);
    const id = selectedStructure?.id || data?.id;

    const [tab, setTab] = useState(0);
    const [current, setCurrent] = useState(0);
    const [form, setForm] = useState({});
    
    const onProceed = () => {
        const emptyItem = form?.salary_breakdown?.find((item) => !item?.salary_component?.length || Number(item.percentage) < 0);
        const result = form?.salary_breakdown?.reduce((acc, item) => acc + Number(item.percentage), 0);
        const pensionActive = form?.deductions?.pension_deduction?.is_enabled

        if(!form?.name) {
            toast.error('Please enter a pay structure name')
            setCurrent(0)
        } else if (!lettersRegex.test(form?.name)) {
            toast.error('Pay structure name must contain letters')
            setCurrent(0)
        } else if(form?.name?.length < 3) {
            toast.error('Pay structure name be at least 3 characters')
            setCurrent(0)
        } else if(emptyItem) {
            toast.error('Please fill or delete empty fields')
            setCurrent(0);
            setTab(0);
        } else if(result !== 100) {
            toast.error('Total salary breakdown must be 100%')
            setCurrent(0);
            setTab(0);
        } else {
            const removeIDs = (data) => {
            return data?.map((item) => {
            const newObj = {};
            for (let key in item) {
                if (key !== "id") {
                    newObj[key] = item[key];
                }
            }
                return newObj;
            })
            }
            const payload = {
                ...form,
                salary_breakdown: removeIDs(form?.salary_breakdown?.map((item) => ({...item, percentage: Number(item.percentage)}))),
                deductions: {
                    ...form.deductions,
                    custom_deductions: removeIDs(customDeductions),
                    pension_deduction: {
                        ...form.deductions.pension_deduction,
                        pension_scheme: pensionActive ? pensionShared ? "EMPLOYER_10_PERCENT_EMPLOYEE_8_PERCENT" : "GROUP_20" : null
                    }
                }
            }
            dispatch(updatePayStructure(payload));
        }
    }

    const items = [
        {
            title: 'Salary Breakdown',
            Component: <ViewSalaryBreakdown
                            form={form}
                            setForm={setForm}
                            setTab={setTab}
                            isViewing={isViewing}
                            breakdownSubmitRef={breakdownSubmitRef}
                        />
        },
        {   title: 'Deduction',
            Component: <ViewDeductionsTab
                            data={data}
                            isViewing={isViewing}
                            deductionSubmitRef={deductionSubmitRef}
                            modalOpen={modalOpen}
                            setModalOpen={setModalOpen}
                            form={form}
                            setForm={setForm}
                            pensionShared={pensionShared}
                            setPensionShared={setPensionShared}
                        />
        }
    ]

    const infoItems = [
        {title: "Pay Structure Details", Component: (
            <form onSubmit={(e) => e.preventDefault()}>
                <div className="border-b w-full pt-7 lg:pt-0">
                    <div className="structure-wrap">
                        <p className="structure-text">Structure Name</p>
                        <div className="structure-input">
                        <input minLength={3} maxLength={50} disabled={isViewing} className="form-input" type="text" required placeholder="Enter Structure Name"
                        value={form.name || ""} onChange={(e) => setForm({...form, name: e.target.value})} />
                        </div>
                    </div>
                </div>
                <button ref={breakdownSubmitRef} style={{display: 'none'}} type="submit">Continue</button>
            </form> )},
        {title: "Pay Structure List", Component: (
            <div className="w-full bg-white p-5">
                <PaygroupsListTable
                    title={data?.name}
                    isViewing={isViewing}
                    selected={selected}
                    setSelected={setSelected}
                    payGroupsData={structurePayGroupsData}
                    payGroupsLoading={structurePayGroupsLoading}
                    onMount={(params) => dispatch(getPayStructureGroups({id, ...params}))}
                    updateLoading={assignStructureGroupsLoading || unassignStructureGroupsLoading}
                    assignPayGroupFn={() => dispatch(assignPayStructureGroups({
                        pay_structure_id: id,
                        pay_group_ids: selected
                    }))}
                    unassignPayGroupFn={(payload) =>
                        dispatch(unassignPayStructureGroups({
                        pay_structure_id: id,
                        pay_group_ids: payload
                    }))}
                />
            </div>
        )}
    ]
    

    useEffect(() => {
        if(data?.name?.length && selectedStructure?.name?.length) {
            const formatted = data?.salary_breakdown?.map((item, idx) => ({...item, id: idx + 1}))
            const newData = { ...data, salary_breakdown: formatted }
            setForm(newData)
        } else {
            history.push("/payroll-configuration/pay-structure");
        }
        // eslint-disable-next-line
    }, [])

    return (
            <>
                <Maybe condition={!editLoading}>
                    <EditDeductionModal />
                    <CustomDeductionModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
                    <div className="w-full">
                        <div className="border-b flex-btw items-center flex-wrap lg:flex-nowrap pb-5">
                            <div className="w-full lg:w-2/4 lg:mb-0">
                                <p className="mb-0 font-medium prl-bold">{isViewing ? "View" : "Edit"} Pay structure</p>
                            </div>
                                <div className="w-full lg:w-2/4 flex items-center lg:justify-end">
                                    <Maybe condition={isViewing === false}>
                                        <button 
                                            onClick={() => onProceed()}
                                        className="btn btn-md btn-ep-primary mr-4 lg:mr-6">
                                            Update Structure
                                        </button>
                                    </Maybe>

                                    <Maybe condition={isViewing === true}>
                                        <button onClick={() => setIsViewing(false)} className="btn btn-md btn-ep-primary mr-5">
                                            <span className='flex items-center pr-3'>
                                                <EditIcon className='mr-3' /> Edit
                                            </span>
                                        </button>
                                    </Maybe>
                                    
                                    <button onClick={() => history.push("/payroll-configuration/pay-structure")} className="btn btn-md btn-transparent-primary">
                                        Cancel
                                    </button>
                                </div>
                        </div>

                        <div className='w-full pt-4 pb-6'>
                                <div className="w-full py-4 flex items-center border-b">
                                {infoItems.map(({title},idx) => {
                                const isActive = current === idx
                                return <div key={idx} className={`mr-6 text-sm cursor-pointer ${isActive ? 'relative text-ep-primary-600 font-medium' : 'text-[#7a8096]'}`}>
                                        <p onClick={() => setCurrent(idx)} className="mb-0">{title}</p>
                                    {isActive && <div style={{height: '2px', bottom: '-14px'}} className='absolute w-full left-0 bg-ep-primary-600' />}
                                </div>
                                })}
                                </div>
                            <div className="mt-6">{infoItems[current].Component}</div>
                        </div>

                        <Maybe condition={current === 0}>
                            <div className='w-full pt-4'>
                                    <div className="w-full bg-ep-primary-light-100 py-4 pl-4 flex items-center">
                                    {items.map(({title}, idx) => {
                                    const isActive = tab === idx
                                    return (
                                        <div onClick={() => setTab(idx)} key={idx} className={`mr-6 cursor-pointer text-sm ${isActive ? 'relative text-ep-primary-600 font-medium' : 'text-[#7a8096]'}`}>
                                            <p className="mb-0">{title}</p>
                                            {isActive && <div style={{height: '2px', bottom: '-8px'}} className='absolute w-full left-0 bg-ep-primary-600' />}
                                        </div>
                                    )
                                    })}
                                    </div>
                                <div className="mt-6">{items[tab].Component}</div>
                            </div>
                        </Maybe>
                        
                    </div>
                </Maybe>

                <Maybe condition={editLoading}>
                    <Spinner message="Updating" />
                </Maybe>
            </>
    )
}

export default ViewStructureDetails